import { Controller } from "@hotwired/stimulus"
import Shepherd from "shepherd.js"
import { offset } from '@floating-ui/dom'
import { AttendanceTour } from "../../utils/attendance_tour"

// Connects to data-controller="tours--attendance"
//
// Walks a user through the attendance report. When the company has not
// finished setting up categories, the tour explains the setup steps and hands
// off to Settings, where tours--attendance-setup continues it. Once reports
// are visible, it highlights each figure and the config button instead.
export default class extends Controller {
  static values = {
    ready: { type: Boolean, default: false },
    rockRms: { type: Boolean, default: false },
    chms: { type: String, default: '' },
    settingsUrl: { type: String, default: '/settings/headcount_categories' },
    // Whether the user may open Settings › Attendance themselves
    canManageSettings: { type: Boolean, default: false },
    // True when arriving back from the settings tour
    resumed: { type: Boolean, default: false },
    // True when a dismissal already exists so we don't record another
    dismissed: { type: Boolean, default: false }
  }

  connect() {
    this.build()
    // Listen on the tour instance rather than the global Shepherd emitter so
    // the handlers do not outlive this tour and fire for a later one.
    this.tour.once('complete', this.createDismissal.bind(this))
    this.tour.once('cancel', this.createDismissal.bind(this))
    this.tour.start()
  }

  disconnect() {
    if (this.tour?.isActive()) { this.tour.cancel() }
  }

  build() {
    this.tour = new Shepherd.Tour({
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        // Override default z-index to ensure it is behind side_modal
        classes: 'box box--card z-9998',
        scrollTo: { behavior: 'smooth', block: 'center' }
      },
      useModalOverlay: true
    })

    this.addIntroStep()

    if (this.readyValue) {
      this.addReportSteps()
    } else {
      this.addSetupSteps()
    }
  }

  addIntroStep() {
    this.tour.addStep({
      id: 'intro',
      title: "Attendance",
      text: this.introText,
      buttons: [this.skipButton, this.nextButton]
    })
  }

  get introText() {
    if (this.resumedValue) {
      return `Your attendance reports are ready!<i class='ml-2 text-amber-300 fa-duotone fa_icon fa-sparkles'></i>
        <br><br>Here's a quick look at what each one shows.`
    }

    return `Attendance reports are here!<i class='ml-2 text-amber-300 fa-duotone fa_icon fa-sparkles'></i>
      <br><br>See how many people show up each week, compare attendance across your ministries,
      and connect headcounts to giving.`
  }

  addSetupSteps() {
    this.tour.addStep({
      id: 'events',
      title: "Step 1: Events",
      text: this.eventsText,
      attachTo: { element: '#attendance_setup', on: 'top' },
      modalOverlayOpeningPadding: 4,
      modalOverlayOpeningRadius: 12,
      floatingUIOptions: { middleware: [offset(10)] },
      buttons: [this.backButton, this.nextButton]
    })

    this.tour.addStep({
      id: 'categories',
      title: "Step 2: Categories",
      text: `Group your events into categories like <em>Adult Worship</em> or <em>Youth Ministry</em>.
        Reports total attendance by category, so pick groupings that match how you think about your ministries.`,
      attachTo: { element: '#attendance_setup', on: 'top' },
      modalOverlayOpeningPadding: 4,
      modalOverlayOpeningRadius: 12,
      floatingUIOptions: { middleware: [offset(10)] },
      buttons: [this.backButton, this.nextButton]
    })

    if (this.canManageSettingsValue) {
      this.addHandoffStep()
    } else {
      this.addAskAdminStep()
    }
  }

  addHandoffStep() {
    this.tour.addStep({
      id: 'settings',
      title: "Head to Settings",
      text: `Both steps happen under <strong>Settings &rsaquo; Attendance</strong>.
        Click <strong>Go to Settings</strong> and we'll walk you through the rest.`,
      attachTo: { element: '#attendance_setup_button', on: 'bottom' },
      // Route the click through the tour button so the settings tour knows to start
      canClickTarget: false,
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 8,
      floatingUIOptions: { middleware: [offset(10)] },
      buttons: [
        this.backButton,
        { action: this.handoff.bind(this), text: 'Go to Settings', classes: 'btn btn-solid' }
      ]
    })
  }

  addAskAdminStep() {
    this.tour.addStep({
      id: 'settings',
      title: "Ask an Admin",
      text: `Both steps happen under <strong>Settings &rsaquo; Attendance</strong>, which needs admin access.
        Ask an admin to select events and build categories, then come back here to see your reports.
        <br><br>Thanks for using MortarStone!`,
      attachTo: { element: '#attendance_setup_button', on: 'bottom' },
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 8,
      floatingUIOptions: { middleware: [offset(10)] },
      buttons: [this.backButton, this.finishButton]
    })
  }

  addReportSteps() {
    this.tour.addStep({
      id: 'headcounts',
      title: "Overall Attendance",
      text: "Total headcounts across your selected categories over time, with a table of the numbers behind the chart.",
      attachTo: { element: '#attendance_headcounts', on: 'top' },
      modalOverlayOpeningPadding: 4,
      modalOverlayOpeningRadius: 12,
      floatingUIOptions: { middleware: [offset(10)] },
      buttons: [this.backButton, this.nextButton]
    })

    this.tour.addStep({
      id: 'by-category',
      title: "Attendance by Category",
      text: "Compare how each category trends so you can spot which ministries are growing.",
      attachTo: { element: '#attendance_by_category', on: 'top' },
      modalOverlayOpeningPadding: 4,
      modalOverlayOpeningRadius: 12,
      floatingUIOptions: { middleware: [offset(10)] },
      buttons: [this.backButton, this.nextButton]
    })

    this.tour.addStep({
      id: 'giving',
      title: "Attendance and Giving",
      text: `<strong>Giving per Attendee</strong> divides giving by headcount, and
        <strong>Attendees per Gift</strong> shows how many people attend for every gift received.
        Together they connect who shows up with who gives.`,
      attachTo: { element: '#attendance_giving', on: 'top' },
      modalOverlayOpeningPadding: 4,
      modalOverlayOpeningRadius: 12,
      floatingUIOptions: { middleware: [offset(10)] },
      buttons: [this.backButton, this.nextButton]
    })

    this.tour.addStep({
      id: 'config',
      title: "Attendance Settings",
      text: `Choose which categories, date range, funds, and locations these reports use.
        <br><br>To add or reorganize events and categories, visit
        <a href="${this.settingsUrlValue}" class="underline">Settings &rsaquo; Attendance</a>.
        <br><br>Thanks for using MortarStone!`,
      attachTo: { element: '#attendance_settings_button', on: 'left' },
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 8,
      floatingUIOptions: { middleware: [offset(10)] },
      buttons: [this.backButton, this.finishButton]
    })
  }

  get eventsText() {
    const source = this.chmsValue ? `from ${this.chmsValue}` : 'from your church management system'

    if (this.rockRmsValue) {
      return `MortarStone imports Weekly Metrics ${source}. Select which metrics should count toward attendance;
        each one becomes an event here.`
    }

    return `MortarStone imports headcounts ${source}. Each service or event you count becomes an event here,
      so there's nothing to enter by hand.`
  }

  // Marks the settings tour as pending, records the dismissal so this page's
  // tour doesn't repeat, then navigates to Settings › Attendance.
  handoff() {
    AttendanceTour.startSetup()
    this.tour.complete()
    Turbo.visit(this.settingsUrlValue)
  }

  get skipButton() {
    return { action: this.tour.cancel, text: 'Skip', classes: 'btn btn-link-slate' }
  }

  get backButton() {
    return { action: this.tour.back, text: 'Back', classes: 'btn btn-link-slate' }
  }

  get nextButton() {
    return { action: this.tour.next, text: 'Next', classes: 'btn btn-solid' }
  }

  get finishButton() {
    return { action: this.tour.complete, text: 'Finish', classes: 'btn btn-solid' }
  }

  createDismissal() {
    if (this.dismissedValue) return

    fetch(
      this.url,
      {
        method: 'POST',
        body: JSON.stringify(this.body),
        credentials: 'include',
        // Survive the Turbo visit that immediately follows a hand-off
        keepalive: true,
        headers: this.headers
      }
    )
  }

  get body() {
    return { dismissal: { kind: 'attendance' } }
  }

  get url() {
    return "/dismissals"
  }

  get headers() {
    const headers = {
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json'
    }

    if (this.csrfToken) { headers['X-CSRF-TOKEN'] = this.csrfToken }

    return headers
  }

  // The meta tag is absent when forgery protection is disabled (e.g. test env)
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }
}
